/**
 * QUIPWA — Hybrid Logical Clock (HLC)
 *
 * Every replicated row carries an `updated_at` HLC timestamp. Comparing two of
 * them answers "which write happened later?" without trusting that devices
 * agree on the time:
 *
 *  - it follows the wall clock when clocks are sane (readable, sortable);
 *  - it never goes backwards on one device, even if the wall clock does;
 *  - after observing a remote timestamp, every local timestamp is greater, so
 *    an edit made *after* syncing always beats what was synced — even if this
 *    device's clock is behind.
 *
 * Format (fixed width, so plain string comparison = chronological order):
 *
 *     0001790000000123-0007-a1b2c3d4
 *     └ wall ms (16) ┘ └ctr┘ └ node ┘
 *
 * The empty string sorts before every timestamp and stands for "written by an
 * app version that did not stamp rows".
 *
 * Reference: Kulkarni et al., "Logical Physical Clocks" (2014).
 */

const WALL_DIGITS = 16
const COUNTER_DIGITS = 4
const MAX_COUNTER = 10 ** COUNTER_DIGITS - 1
/** Refuse remote timestamps more than a day ahead: a broken clock must not poison ours. */
export const MAX_DRIFT_MS = 24 * 60 * 60 * 1000

export interface HlcParts {
  wall: number
  counter: number
  node: string
}

export function formatHlc({ wall, counter, node }: HlcParts): string {
  return `${String(wall).padStart(WALL_DIGITS, '0')}-${String(counter).padStart(COUNTER_DIGITS, '0')}-${node}`
}

export function parseHlc(value: string): HlcParts | null {
  const m = /^(\d{16})-(\d{4})-(.+)$/.exec(value)
  if (!m) return null
  return { wall: Number(m[1]), counter: Number(m[2]), node: m[3]! }
}

/** Chronological comparison; '' (unstamped) is the oldest possible value. */
export function compareHlc(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}

/** The later of two timestamps. */
export function maxHlc(a: string, b: string): string {
  return compareHlc(a, b) >= 0 ? a : b
}

/** Wall-clock milliseconds encoded in a timestamp (0 for unstamped). */
export function hlcWallMs(value: string): number {
  return parseHlc(value)?.wall ?? 0
}

export class HybridClock {
  readonly node: string
  private last: HlcParts
  private readonly nowMs: () => number

  constructor(node: string, nowMs: () => number = Date.now) {
    if (!/^[A-Za-z0-9_]+$/.test(node)) throw new Error(`Invalid HLC node id: ${node}`)
    this.node = node
    this.nowMs = nowMs
    this.last = { wall: 0, counter: 0, node }
  }

  /** A timestamp strictly greater than every one this clock has produced or observed. */
  now(): string {
    const physical = this.nowMs()
    if (physical > this.last.wall) {
      this.last = { wall: physical, counter: 0, node: this.node }
    } else {
      this.bumpCounter()
    }
    return formatHlc(this.last)
  }

  /**
   * Merge a timestamp seen in replicated data, so that later local writes sort
   * after it. Timestamps implausibly far in the future are ignored.
   */
  observe(remote: string): void {
    const parsed = parseHlc(remote)
    if (!parsed) return
    if (parsed.wall > this.nowMs() + MAX_DRIFT_MS) return
    if (parsed.wall > this.last.wall) {
      this.last = { wall: parsed.wall, counter: parsed.counter, node: this.node }
    } else if (parsed.wall === this.last.wall && parsed.counter > this.last.counter) {
      this.last = { ...this.last, counter: parsed.counter }
    }
  }

  private bumpCounter(): void {
    if (this.last.counter >= MAX_COUNTER) {
      // 10 000 events in one millisecond: borrow the next millisecond.
      this.last = { wall: this.last.wall + 1, counter: 0, node: this.node }
    } else {
      this.last = { ...this.last, counter: this.last.counter + 1 }
    }
  }
}
